import { cloneDeep } from "lodash";

import { normalizeSchemaDefinition } from "./normalize-schema-definition";
import { hasSchemaType } from "../../extensions/json-schema/schemaFormModel";

const schemaProperties = (schema) => schema?.properties ?? {};
const isSchemaPropertyRequired = (schema, property) => schema?.required?.includes(property) ?? false;
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);
const hasObjectValue = (value) => value !== null
	&& value !== undefined
	&& typeof value === "object"
	&& Object.keys(value).length > 0;
const isPlainObject = (value) => Object.prototype.toString.call(value) === "[object Object]";

function isEmptyArrayItem(value) {
	if(value === null || value === undefined) {
		return true;
	}

	if(typeof value === "string") {
		return value.trim() === "";
	}

	if(Array.isArray(value)) {
		return value.length === 0;
	}

	if(isPlainObject(value)) {
		return Object.values(value).every(isEmptyArrayItem);
	}

	return false;
}

function pruneEmptyArrayItems(value) {
	if(Array.isArray(value)) {
		return value.reduce((items, item) => {
			const nextItem = pruneEmptyArrayItems(item);

			if(!isEmptyArrayItem(nextItem)) {
				items.push(nextItem);
			}

			return items;
		}, []);
	}

	if(isPlainObject(value)) {
		return Object.entries(value).reduce((nextValue, [key, childValue]) => {
			nextValue[key] = pruneEmptyArrayItems(childValue);
			return nextValue;
		}, {});
	}

	return cloneDeep(value);
}

function cloneEntityValue(entity, key, fallback) {
	return hasOwn(entity, key) ? pruneEmptyArrayItems(entity[key]) : cloneDeep(fallback);
}

export function getEntitySchemaSection(schema, fallbackTitle) {
	const properties = Object.entries(schemaProperties(schema));
	if(properties.length !== 1) {
		return { title: fallbackTitle };
	}

	const [key, propertySchema] = properties[0];
	if(!hasSchemaType(propertySchema, "array") || !hasSchemaType(propertySchema.items, "object")) {
		return { title: fallbackTitle };
	}

	return {
		key,
		title: propertySchema.title || key
	};
}

export function buildEntitySchemaEntries(schema, entity, $sce) {
	return Object.entries(schemaProperties(schema)).map(([propName, rawSubSchema]) => {
		const normalizedSubSchema = normalizeSchemaDefinition(rawSubSchema, $sce);
		const title = rawSubSchema.title || propName;
		const required = isSchemaPropertyRequired(schema, propName);

		if(hasSchemaType(rawSubSchema, "object")) {
			const objectSchema = { ...normalizedSubSchema };
			const section = getEntitySchemaSection(rawSubSchema, title);
			delete objectSchema.title;

			return {
				key: propName,
				title,
				schema: objectSchema,
				value: cloneEntityValue(entity, propName, {}),
				...(required ? { required: true } : {}),
				section
			};
		}

		const entry = {
			key: propName,
			title,
			schema: normalizedSubSchema,
			...(required ? { required: true } : {})
		};

		if(hasOwn(entity, propName)) {
			entry.value = cloneEntityValue(entity, propName);
		}

		return entry;
	});
}

export function mergeEntityInlineSchemaModel(entity, inlineSchema, inlineSchemaModel) {
	return Object.keys(schemaProperties(inlineSchema)).reduce((nextEntity, propName) => {
		if(hasOwn(inlineSchemaModel, propName)) {
			nextEntity[propName] = pruneEmptyArrayItems(inlineSchemaModel[propName]);
		}
		else {
			delete nextEntity[propName];
		}

		return nextEntity;
	}, { ...entity });
}

export function mergeEntitySchemaEntries(entity, entries) {
	return (entries ?? []).reduce((nextEntity, entry) => {
		if(entry.section && !hasOwn(entity, entry.key) && !hasObjectValue(entry.value)) {
			return nextEntity;
		}

		if(hasOwn(entry, "value")) {
			nextEntity[entry.key] = pruneEmptyArrayItems(entry.value);
		}
		else {
			delete nextEntity[entry.key];
		}

		return nextEntity;
	}, { ...entity });
}
